import React from "react";
import { Calendar, User, Clock, Tag, Share2, ChevronLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div data-section-id="30969" className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-8 animate-pulse">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-96 bg-muted rounded-xl w-full" />
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="bg-destructive/10 text-destructive p-6 rounded-xl inline-block max-w-md">
          <h3 className="text-lg font-semibold mb-2">Error Loading Article</h3>
          <p className="mb-4">We couldn't retrieve the health insight. Please try refreshing.</p>
          <Button onClick={refetch} variant="outline" className="rounded-full border-accent text-accent hover:bg-accent hover:text-accent-foreground">Retry</Button>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-muted-foreground font-serif">Article Not Found</h2>
        <div className="mt-6">
          <Link to="/HomePage">
            <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">Back to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 py-12 max-w-4xl">
      {}
      <div className="mb-8">
        <Link to="/HealthInsightsPage">
          <Button variant="ghost" size="sm" className="pl-0 hover:bg-transparent hover:text-accent text-muted-foreground transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Insights
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-10 text-center md:text-left">
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4 justify-center md:justify-start">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent/10 text-accent font-medium tracking-wide">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center">
            <Calendar className="w-4 h-4 mr-1.5 text-accent" />
            {formatDate(post.date)}
          </span>
          <span className="flex items-center">
            <Clock className="w-4 h-4 mr-1.5 text-accent" />
            {Math.ceil((post.content?.length || 0) / 1500)} min read
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground leading-tight mb-6 tracking-tight">
          {post.title}
        </h1>

        {}
        {post.author && (
          <div className="flex items-center justify-center md:justify-start gap-3">
            <Avatar className="h-10 w-10 border border-border shadow-sm">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="bg-muted text-muted-foreground"><User className="w-5 h-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground tracking-normal">{post.author.name}</p>
              <p className="text-xs text-muted-foreground">Practitioner</p>
            </div>
          </div>
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl bg-muted border border-border/50">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[600px] transition-transform duration-700 hover:scale-105"
          />
        </div>
      )}

      {}
      <div 
        className="prose prose-lg dark:prose-invert max-w-none 
        prose-headings:font-serif prose-headings:font-bold prose-headings:text-foreground prose-headings:tracking-tight
        prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6
        prose-a:text-accent prose-a:no-underline hover:prose-a:underline
        prose-img:rounded-2xl prose-img:shadow-lg
        prose-blockquote:border-l-4 prose-blockquote:border-accent prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-foreground prose-blockquote:bg-accent/5 prose-blockquote:py-2
        prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-6
        prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-6
        prose-li:mb-2"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12 opacity-50" />

      {}
      <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12">
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="w-4 h-4 text-accent" />
            <span className="text-sm font-medium mr-2 text-foreground">Tags:</span>
            {post.tags.map((tag) => (
              <Link key={tag.id} to={tag.link || "#"}>
                <span className="text-xs bg-muted text-secondary px-3 py-1 rounded-full hover:bg-accent hover:text-accent-foreground transition-all duration-300">
                  #{tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Share this insight:</span>
          <Button variant="outline" size="icon" className="rounded-full w-9 h-9 border-border hover:border-accent hover:text-accent transition-colors">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {}
      {post.author && (
        <Card className="bg-muted/40 border border-border/50 shadow-md rounded-2xl overflow-hidden">
          <CardContent className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-8">
            <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="text-3xl bg-accent/10 text-accent"><User /></AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left space-y-3 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-xl font-bold font-serif text-foreground tracking-tight">About {post.author.name}</h3>
                  <p className="text-sm text-accent font-medium">Wellness Specialist & Practitioner</p>
                </div>
                {post.author.link && (
                  <Link to={post.author.link}>
                    <Button variant="outline" size="sm" className="rounded-full border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-all">View Profile</Button>
                  </Link>
                )}
              </div>
              <p className="text-muted-foreground leading-relaxed text-base">
                Experienced healthcare professional dedicated to providing clear insights into musculoskeletal health and holistic wellness. 
                Committed to helping the Cyprus community move better and live healthier through personalized chiropractic and acupuncture care.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || {};
  
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background w-full text-foreground font-sans">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}